// ═══════════════════════════════════════════════════════════════
// ConfirmDialog — themed replacement for native window.confirm().
// Drop-in pattern: stash the pending action in state, render the
// dialog when state is set, fire the action on confirm.
//
// Native confirm() works but reads as "browser dialog from 1998"
// and breaks immersion against the rest of the app's design.
// This component matches modal-box / modal-header / btn-link /
// btn already used elsewhere.
//
// Variants:
//   tone='default'  → orange Confirm button (the standard CTA)
//   tone='danger'   → red Confirm button (delete-style action)
//
// Props:
//   title         — heading
//   body          — main message (string, can include \n)
//   confirmLabel  — Confirm button text (default "Confirm")
//   cancelLabel   — Cancel button text (default "Cancel")
//   tone          — 'default' | 'danger'
//   onConfirm     — fires when user clicks Confirm
//   onCancel      — fires when user clicks Cancel / X / overlay
// ═══════════════════════════════════════════════════════════════
import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

export default function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel  = 'Cancel',
  tone         = 'default',
  onConfirm,
  onCancel,
}) {
  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className="modal-box confirm-dialog" role="alertdialog" aria-modal="true">
        <div className="modal-header">
          <h2>
            <AlertTriangle
              size={18}
              style={{
                verticalAlign: '-3px',
                marginRight: 6,
                color: tone === 'danger' ? 'var(--danger)' : 'var(--accent)',
              }}
            />
            {title}
          </h2>
          <button type="button" className="btn-link" onClick={onCancel} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p className="confirm-dialog-body">{body}</p>
        <div className="form-actions">
          <button type="button" className="btn-link" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 'btn confirm-dialog-danger' : 'btn'}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
